import { useCallback, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { BareFetcher, SWRConfiguration, SWRResponse } from 'swr'
import { useSWRConfig } from 'swr'
import { getPageCommentsKey, usePageComments } from 'modules/comments/api/get-page-comments'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import {
  handleTargetComment,
  parseParentIdFromKey,
} from 'modules/comments/components/comments-list/utils'
import type { CommentSortEnum, CommentsTypeEnum } from 'modules/comments/types'
import { getCommentSortPreference } from 'modules/comments/utils/comment-preferences'
import { getDefaultSortOption } from 'modules/comments/utils/comment-sorting'
import { mergeComments } from 'modules/comments/utils/merge-comments'

interface UseCommentsWithCacheManagementProps {
  commentsType: CommentsTypeEnum
  pageId: number
  shouldFetch?: boolean
  onError?: (error: Error) => void
  targetCommentId?: number
  isCommunityComments: boolean
  setHighlightedComment: (comment: CommentInterface | null) => void
  highlightedComment: CommentInterface | null
}

interface UpdateRepliesProps {
  commentId: number
  replies?: CommentInterface[]
}

interface UseCommentsWithCacheManagementResult {
  comments: CommentInterface[]
  replies: Record<number, { replies: CommentInterface[] }>
  sortOption: CommentSortEnum
  requestParams: {
    lastCommentId: number
    parentId: number | null
  }
  updateReplies: ({ commentId, replies }: UpdateRepliesProps) => void
  handleSortChange: (sortOption: CommentSortEnum) => void
  handleLoadMoreGap: ({
    lastCommentId,
    parentId,
  }: {
    lastCommentId: number
    parentId: number | null
  }) => void
  getPageCommentsQuery: SWRResponse<
    CommentInterface[],
    unknown,
    SWRConfiguration<CommentInterface[], unknown, BareFetcher<CommentInterface[]>> | undefined
  >
  setComments: Dispatch<SetStateAction<CommentInterface[]>>
  isReloading: boolean
}

export function useCommentsWithCacheManagement({
  commentsType,
  pageId,
  shouldFetch = true,
  onError,
  targetCommentId,
  isCommunityComments,
  setHighlightedComment,
  highlightedComment,
}: UseCommentsWithCacheManagementProps): UseCommentsWithCacheManagementResult {
  const { mutate } = useSWRConfig()

  const [sortOption, setSortOption] = useState<CommentSortEnum>(() => {
    const savedPreference = getCommentSortPreference()
    return savedPreference || getDefaultSortOption(isCommunityComments)
  })
  const [initialTargetCommentId, setInitialTargetCommentId] = useState<number | undefined>(
    targetCommentId,
  )
  const [comments, setComments] = useState<CommentInterface[]>([])
  const [replies, setReplies] = useState<{
    [parentCommentId: number]: { replies: CommentInterface[] }
  }>({})
  const [requestParams, setRequestParams] = useState<{
    lastCommentId: number
    parentId: number | null
  }>({ lastCommentId: 0, parentId: null })

  const shouldResetReplies = requestParams.lastCommentId === 0

  const updateReplies = useCallback(({ commentId, replies }: UpdateRepliesProps) => {
    setReplies(prev => {
      const prevComment = prev[commentId] || {
        replies: [] as CommentInterface[],
      }
      return {
        ...prev,
        [commentId]: {
          replies: replies ?? prevComment.replies,
        },
      }
    })
  }, [])

  const getPageCommentsQuery = usePageComments({
    parentId: requestParams.parentId,
    commentsType,
    pageId,
    lastCommentId: requestParams.lastCommentId,
    targetCommentId:
      requestParams.lastCommentId === 0 && !requestParams.parentId && !!initialTargetCommentId
        ? initialTargetCommentId
        : undefined,
    sortOption,
    config: {
      revalidateOnMount: shouldFetch,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess: (data, key) => {
        const { isReply, parentId } = parseParentIdFromKey(key)

        if (isReply) {
          updateReplies({
            commentId: parentId,
            replies: mergeComments([...(replies[parentId]?.replies ?? [])], data ?? []),
          })
        } else {
          setComments(prev => {
            if (shouldResetReplies) {
              return data ?? []
            }
            return mergeComments(prev, data ?? [])
          })

          if (shouldResetReplies) {
            setReplies({})
          }

          data?.forEach(comment => {
            if (comment.children) {
              updateReplies({
                commentId: comment.id,
                replies: mergeComments(
                  shouldResetReplies ? [] : [...(replies[comment.id]?.replies ?? [])],
                  comment.children,
                ),
              })
            }
          })
        }

        handleTargetComment(
          data,
          initialTargetCommentId,
          setHighlightedComment,
          highlightedComment,
          setInitialTargetCommentId,
        )
      },
      onError: (error, key) => {
        const { isReply, parentId } = parseParentIdFromKey(key)

        if (isReply) {
          updateReplies({ commentId: parentId, replies: [] })
        }

        if (onError && error instanceof Error) onError(error)
      },
    },
  })

  const isReloading =
    getPageCommentsQuery.isLoading &&
    requestParams.parentId === null &&
    requestParams.lastCommentId === 0

  const clearCacheAndRefresh = useCallback(
    (sortOption: CommentSortEnum) => {
      const oldCacheKey = getPageCommentsKey({
        parentId: requestParams.parentId,
        commentsType,
        pageId,
        lastCommentId: requestParams.lastCommentId,
        sortOption,
        targetCommentId:
          requestParams.lastCommentId === 0 && !requestParams.parentId && !!initialTargetCommentId
            ? initialTargetCommentId
            : undefined,
      })

      setRequestParams({ lastCommentId: 0, parentId: null })

      const _cacheKey = getPageCommentsKey({
        parentId: null,
        commentsType,
        pageId,
        lastCommentId: 0,
        sortOption: sortOption,
        targetCommentId:
          requestParams.lastCommentId === 0 && !requestParams.parentId && !!initialTargetCommentId
            ? initialTargetCommentId
            : undefined,
      })

      mutate(oldCacheKey, undefined, { revalidate: false })
      mutate(_cacheKey, undefined, { revalidate: true })
    },
    [
      requestParams.parentId,
      requestParams.lastCommentId,
      commentsType,
      pageId,
      sortOption,
      initialTargetCommentId,
      mutate,
    ],
  )

  const handleSortChange = useCallback(
    (updatedSortOption: CommentSortEnum) => {
      if (updatedSortOption === sortOption) return

      setSortOption(updatedSortOption)
      clearCacheAndRefresh(updatedSortOption)
    },
    [sortOption, clearCacheAndRefresh],
  )

  const handleLoadMoreGap = useCallback(
    ({ lastCommentId, parentId }: { lastCommentId: number; parentId: number | null }) => {
      if (parentId) {
        updateReplies({ commentId: parentId })
        setRequestParams({ lastCommentId, parentId })
      } else {
        setRequestParams({ lastCommentId, parentId: null })
      }
    },
    [updateReplies],
  )

  return {
    comments,
    replies,
    sortOption,
    requestParams,
    updateReplies,
    handleSortChange,
    handleLoadMoreGap,
    getPageCommentsQuery,
    setComments,
    isReloading,
  }
}
